import { create } from 'zustand';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfg = { withCredentials: true };

export const TRASH_DAYS = 30;

export const isTrashed = (p) => !!p.deleted_at;
export const isArchived = (p) => !!p.archived_at && !p.deleted_at;
export const isActive = (p) => !p.deleted_at && !p.archived_at;

export const filterProjects = (projects, filter, search) => {
  const q = (search || '').trim().toLowerCase();
  return projects.filter((p) => {
    if (q && !`${p.name} ${p.address || ''}`.toLowerCase().includes(q)) return false;
    if (filter === 'trash') return isTrashed(p);
    if (filter === 'archived') return isArchived(p);
    if (filter === 'published') return isActive(p) && !!p.is_public;
    if (filter.startsWith('folder:')) return isActive(p) && p.folder_id === filter.slice(7);
    return isActive(p);
  });
};

export const useProjectsHubStore = create((set, get) => ({
  projects: [],
  folders: [],
  loading: true,
  filter: 'all',
  search: '',
  selectedId: null,
  checked: [],
  dragIds: [],

  setFilter: (filter) => set({ filter, checked: [], selectedId: null }),
  setSearch: (search) => set({ search }),
  select: (id) => set((s) => ({ selectedId: s.selectedId === id ? null : id })),
  closePanel: () => set({ selectedId: null }),
  toggleCheck: (id) => set((s) => ({ checked: s.checked.includes(id) ? s.checked.filter((x) => x !== id) : [...s.checked, id] })),
  setChecked: (ids) => set({ checked: ids }),
  clearChecked: () => set({ checked: [] }),
  setDragIds: (ids) => set({ dragIds: ids }),

  fetchAll: async () => {
    try {
      const [p, f] = await Promise.all([axios.get(`${API}/projects/summary`, cfg), axios.get(`${API}/project-folders`, cfg)]);
      set({ projects: p.data, folders: f.data, loading: false });
    } catch {
      toast.error('Erreur lors du chargement des projets');
      set({ loading: false });
    }
  },

  createFolder: async (name) => {
    const r = await axios.post(`${API}/project-folders`, { name }, cfg);
    set((s) => ({ folders: [...s.folders, r.data] }));
    return r.data;
  },
  renameFolder: async (folderId, name) => {
    await axios.put(`${API}/project-folders/${folderId}`, { name }, cfg);
    set((s) => ({ folders: s.folders.map((f) => (f.folder_id === folderId ? { ...f, name } : f)) }));
  },
  deleteFolder: async (folderId) => {
    await axios.delete(`${API}/project-folders/${folderId}`, cfg);
    set((s) => ({
      folders: s.folders.filter((f) => f.folder_id !== folderId),
      projects: s.projects.map((p) => (p.folder_id === folderId ? { ...p, folder_id: null } : p)),
      filter: s.filter === `folder:${folderId}` ? 'all' : s.filter,
    }));
  },

  organize: async (ids, action, folderId = null) => {
    const r = await axios.post(`${API}/projects/bulk-organize`, { project_ids: ids, action, folder_id: folderId }, cfg);
    const now = new Date().toISOString();
    set((s) => ({
      checked: [],
      dragIds: [],
      selectedId: ['trash', 'delete'].includes(action) && ids.includes(s.selectedId) ? null : s.selectedId,
      projects: action === 'delete'
        ? s.projects.filter((p) => !ids.includes(p.project_id))
        : s.projects.map((p) => {
          if (!ids.includes(p.project_id)) return p;
          if (action === 'move') return { ...p, folder_id: folderId };
          if (action === 'archive') return { ...p, archived_at: now };
          if (action === 'unarchive') return { ...p, archived_at: null };
          if (action === 'trash') return { ...p, deleted_at: now };
          if (action === 'restore') return { ...p, deleted_at: null };
          return p;
        }),
    }));
    return r.data;
  },

  renameProject: async (id, data) => {
    const r = await axios.put(`${API}/projects/${id}`, data, cfg);
    set((s) => ({ projects: s.projects.map((p) => (p.project_id === id ? { ...p, ...r.data } : p)) }));
  },
}));
